import React, { useState } from "react";
import translate from "i18n/translate";

// Extracts minutes / SMS / GB / period from the Arabic title+description.
// Toppinex /api/operators/bundles returns these only as free text — without
// this fallback the gradient cards have no main label.
const parseOoredooBundleText = (item) => {
  const txt = [
    item?.description, item?.ar_des, item?.en_des, item?.he_des,
    item?.title, item?.ar_title, item?.en_title, item?.he_title,
  ].filter(Boolean).join(" ");

  if (!txt) return { minutes: null, sms: null, gb: null, periodLabel: "" };

  const grab = (re) => {
    const m = txt.match(re);
    return m ? m[1] : null;
  };

  const minutes = grab(/(\d+)\s*دقيقة/);
  const sms     = grab(/(\d+)\s*رسالة/);
  const gb      = grab(/(\d+(?:\.\d+)?)\s*(?:جيجا|GB|gb)/);

  let periodLabel = "";
  if      (/شهري|الشهر/.test(txt))                  periodLabel = "شهرية";
  else if (/أسبوع|اسبوع|لأسبوع|للأسبوع/.test(txt))  periodLabel = "أسبوعية";
  else if (/يومي|ليوم|أيام/.test(txt))              periodLabel = "يومية";
  else if (/ساع(?:ي|ة|ات)/.test(txt))               periodLabel = "ساعية";

  return { minutes, sms, gb, periodLabel };
};

// ─── Per-type colour schemes ──────────────────────────────────────────────────
// Layout is identical across types — only colours change. The MIN scheme is
// hand-tuned to mirror the platform-rendered PNG (yellow card, red period pill,
// cream price circle with dark text). Others swap the background and recompute
// text/circle contrasts.
const SCHEMES = {
  MIN:           { bg1:"#FFD80A", bg2:"#FFC107", text:"#1a1a2e", pill:"#D32F2F", pillText:"#fff",    circleBg:"#FFE873", circleText:"#1a1a2e", circleBorder:"#1a1a2e" },
  "3G":          { bg1:"#FFD80A", bg2:"#FFC107", text:"#1a1a2e", pill:"#D32F2F", pillText:"#fff",    circleBg:"#FFE873", circleText:"#1a1a2e", circleBorder:"#1a1a2e" },
  "6Months":     { bg1:"#9575CD", bg2:"#5E35B1", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#5E35B1", circleBorder:"#fff" },
  TikTok:        { bg1:"#1a1a1a", bg2:"#000000", text:"#fff",    pill:"#EE1D52", pillText:"#fff",    circleBg:"#fff",    circleText:"#1a1a1a", circleBorder:"#fff" },
  Social:        { bg1:"#42B72A", bg2:"#1B5E20", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#1B5E20", circleBorder:"#fff" },
  Telegram:      { bg1:"#2AABEE", bg2:"#1c92d2", text:"#fff",    pill:"#fff",    pillText:"#1c92d2", circleBg:"#fff",    circleText:"#1c92d2", circleBorder:"#fff" },
  LocalCalls:    { bg1:"#71B280", bg2:"#2E7D32", text:"#fff",    pill:"#D32F2F", pillText:"#fff",    circleBg:"#fff",    circleText:"#1B5E20", circleBorder:"#fff" },
  "YOUTH-3G":    { bg1:"#9CCC65", bg2:"#558B2F", text:"#1a1a2e", pill:"#D32F2F", pillText:"#fff",    circleBg:"#FFD80A", circleText:"#1a1a2e", circleBorder:"#1a1a2e" },
  "YOUTH-MIN":   { bg1:"#5C6BC0", bg2:"#283593", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#283593", circleBorder:"#fff" },
  Roaming:       { bg1:"#9C27B0", bg2:"#4A148C", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#4A148C", circleBorder:"#fff" },
  LocalRoaming:  { bg1:"#BA68C8", bg2:"#7B1FA2", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#7B1FA2", circleBorder:"#fff" },
  International: { bg1:"#3F51B5", bg2:"#1A237E", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#1A237E", circleBorder:"#fff" },
  Credit:        { bg1:"#EF5350", bg2:"#B71C1C", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#B71C1C", circleBorder:"#fff" },
  ROM:           { bg1:"#26A69A", bg2:"#004D40", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#004D40", circleBorder:"#fff" },
  YOUTH:         { bg1:"#26C6DA", bg2:"#00838F", text:"#fff",    pill:"#FFD80A", pillText:"#1a1a2e", circleBg:"#fff",    circleText:"#00838F", circleBorder:"#fff" },
};

const DEFAULT_SCHEME = SCHEMES.MIN;

// ─── Size presets ─────────────────────────────────────────────────────────────
const SIZE = {
  column3: { card: 160, mainFs: "2rem",    subFs: "1rem",    badgeFs: "0.78rem", priceFs: "2.1rem", unitFs: "0.85rem", cir: 110 },
  column4: { card: 132, mainFs: "1.5rem",  subFs: "0.85rem", badgeFs: "0.62rem", priceFs: "1.55rem", unitFs: "0.65rem", cir: 88  },
  default: { card: 132, mainFs: "1.5rem",  subFs: "0.85rem", badgeFs: "0.62rem", priceFs: "1.55rem", unitFs: "0.65rem", cir: 88  },
  column6: { card: 110, mainFs: "1.05rem", subFs: "0.6rem",  badgeFs: "0.5rem",  priceFs: "1rem",   unitFs: "0.5rem",  cir: 64  },
};

const OoredooBundleCard = ({ item = {}, size = "default", choose = false }) => {
  const scheme = SCHEMES[item.type] || DEFAULT_SCHEME;
  const sc     = SIZE[size] || SIZE.default;

  // Prefer the platform-rendered PNG thumbnail when available. Fall back to
  // the CSS card if the URL is missing or the image 404s (the catalogue ships
  // PNGs only for some bundles — roaming/international are CSS-rendered).
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!item.url && !imageFailed;
  if (showImage) {
    return (
      <img
        src={item.url}
        alt={item.ar_title || item.en_title || item.type || ""}
        onError={() => setImageFailed(true)}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          borderRadius: choose ? "14px" : "20px",
          cursor: "pointer",
        }}
      />
    );
  }

  // ── Data resolution ───────────────────────────────────────────────────────
  const parsed = parseOoredooBundleText(item);

  const displayPrice = item.display_price || item.price || "—";

  const minVal = (item.min && item.min !== "0") ? item.min : parsed.minutes;
  const smsVal = (item.sms && item.sms !== "0") ? item.sms : parsed.sms;
  const gbVal  = (item.gb  && item.gb  !== "0") ? item.gb  : parsed.gb;

  const periodLabel = item.period_label || item.period || parsed.periodLabel || "";
  const title       = item.ar_title || item.en_title || item.type || "";

  const cirSize = choose ? 70 : sc.cir;
  const bg = `linear-gradient(135deg, ${scheme.bg1} 0%, ${scheme.bg2} 100%)`;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        background: bg,
        borderRadius: choose ? "14px" : "20px",
        overflow: "hidden",
        minHeight: choose ? "84px" : `${sc.card}px`,
        width: choose ? "100%" : "auto",
        boxShadow: "0 6px 22px rgba(0,0,0,0.22)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        cursor: "pointer",
        fontFamily: "'Cairo','Tajawal','Segoe UI',system-ui,sans-serif",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-3px) scale(1.012)";
        e.currentTarget.style.boxShadow = "0 14px 32px rgba(0,0,0,0.28)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "0 6px 22px rgba(0,0,0,0.22)";
      }}
    >
      {/* ── Info column (visually right in RTL) ──────────────────────── */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "5px",
        padding: choose ? "10px 14px" : "14px 16px",
        color: scheme.text,
      }}>
        {/* Main: minutes / GB */}
        {minVal && (
          <span style={{
            fontSize: choose ? "1.05rem" : sc.mainFs,
            fontWeight: 900,
            lineHeight: 1.1,
          }}>
            {minVal} دقيقة
          </span>
        )}
        {smsVal && (
          <span style={{
            fontSize: choose ? "0.78rem" : sc.subFs,
            fontWeight: 800,
            lineHeight: 1.1,
            opacity: 0.9,
          }}>
            {smsVal} رسالة
          </span>
        )}
        {!minVal && gbVal && (
          <span style={{
            fontSize: choose ? "1.05rem" : sc.mainFs,
            fontWeight: 900,
            lineHeight: 1.1,
          }}>
            {gbVal} GB
          </span>
        )}
        {!minVal && !gbVal && title && (
          <span style={{
            fontSize: choose ? "0.85rem" : "0.95rem",
            fontWeight: 800,
            lineHeight: 1.2,
          }}>
            {title}
          </span>
        )}

        {/* Period pill */}
        {periodLabel && (
          <span style={{
            display: "inline-block",
            alignSelf: "flex-start",
            background: scheme.pill,
            color: scheme.pillText,
            fontSize: choose ? "0.55rem" : sc.badgeFs,
            fontWeight: 700,
            borderRadius: "999px",
            padding: choose ? "3px 10px" : "4px 12px",
            marginTop: "2px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          }}>
            {periodLabel}
          </span>
        )}
      </div>

      {/* ── Price circle (visually left in RTL) ────────────────────── */}
      <div style={{
        flexShrink: 0,
        padding: choose ? "10px 14px" : "12px 14px",
      }}>
        <div style={{
          width: cirSize,
          height: cirSize,
          borderRadius: "50%",
          background: scheme.circleBg,
          border: `2px solid ${scheme.circleBorder}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.22)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1px",
        }}>
          <span style={{
            color: scheme.circleText,
            fontWeight: 900,
            fontSize: choose ? "1.25rem" : sc.priceFs,
            lineHeight: 1,
          }}>
            {displayPrice}
          </span>
          <span style={{
            color: scheme.circleText,
            fontWeight: 700,
            fontSize: choose ? "0.55rem" : sc.unitFs,
          }}>
            {translate("ILS") || "شيكل"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OoredooBundleCard;
